import { Wand2 } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Textarea from "@/components/ui/Textarea";
import Toggle from "@/components/ui/Toggle";
import { useToast } from "@/components/ui/Toast";
import { INDIAN_STATES } from "@/pages/admin/retailers/data";
import type { Retailer } from "@/pages/admin/retailers/types";

const STATE_OPTIONS = INDIAN_STATES.map((s) => ({ value: s, label: s }));

const BUSINESS_TYPE_OPTIONS = [
  { value: "Proprietorship", label: "Proprietorship (Sole Owner)" },
  { value: "Partnership", label: "Partnership" },
  { value: "Private Limited", label: "Private Limited Company" },
  { value: "LLP", label: "Limited Liability Partnership (LLP)" },
];

interface RetailerFormModalProps {
  open: boolean;
  onClose: () => void;
  retailer: Retailer | null;
  onSave: (data: Partial<Retailer> & { password?: string; businessType?: string }, id?: string) => Promise<void>;
}

function generatePassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 10; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
}

export default function RetailerFormModal({ open, onClose, retailer, onSave }: RetailerFormModalProps) {
  const toast = useToast();
  const isEdit = !!retailer;
  const [saving, setSaving] = useState(false);

  const [ownerName, setOwnerName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [shopName, setShopName] = useState("");
  const [businessType, setBusinessType] = useState("Proprietorship");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [gst, setGst] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setOwnerName(retailer?.name ?? "");
    setMobile(retailer?.mobile ?? "");
    setEmail(retailer?.email ?? "");
    setPassword("");
    setShopName(retailer?.shopName ?? "");
    setBusinessType(retailer?.businessType ?? "Proprietorship");
    setCity(retailer?.city ?? "");
    setState(retailer?.state ?? "");
    setFullAddress(retailer?.fullAddress ?? "");
    setGst(retailer?.gst ?? "");
    setDeviceId(retailer?.deviceId ?? "");
    setIsActive(retailer?.isActive ?? true);
    setErrors({});
  }, [open, retailer]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!ownerName.trim()) e.ownerName = "Owner name is required";
    if (!/^[6-9]\d{9}$/.test(mobile)) e.mobile = "Enter a valid 10-digit mobile number starting with 6-9";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) e.email = "Enter a valid email address";
    if (!isEdit && !password) e.password = "Password is required";
    if (!isEdit && password && password.length < 8) e.password = "Password must be at least 8 characters";
    if (!shopName.trim()) e.shopName = "Shop/Business name is required";
    if (!businessType) e.businessType = "Business type is required";
    if (!city) e.city = "City is required";
    if (!state) e.state = "State is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload: Partial<Retailer> & { password?: string; businessType?: string } = {
        name: ownerName.trim(),
        mobile,
        email: email.trim(),
        shopName: shopName.trim(),
        businessType,
        city,
        state,
        fullAddress,
        gst: gst.toUpperCase(),
        deviceId: deviceId.trim() || undefined,
        isActive,
      };
      if (!isEdit && password) payload.password = password;
      await onSave(payload, retailer?.id);
      onClose();
    } catch (err: any) {
      const msg = err?.message || "Failed to save retailer";
      if (msg.toLowerCase().includes("mobile")) {
        setErrors((prev) => ({ ...prev, mobile: msg }));
      } else if (msg.toLowerCase().includes("email")) {
        setErrors((prev) => ({ ...prev, email: msg }));
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  const ownerNameInput = (
    <Input
      label="Owner Name"
      required
      value={ownerName}
      onChange={(e) => setOwnerName(e.target.value)}
      error={errors.ownerName}
      placeholder="e.g. Rajesh Kumar"
    />
  );

  const mobileInput = (
    <div>
      <span className="mb-1.5 block text-[13px] font-medium text-ink-secondary">
        Mobile Number <span className="text-danger">*</span>
      </span>
      <div className="flex overflow-hidden rounded-lg border border-slate-300 bg-white focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/30">
        <span className="inline-flex items-center border-r border-slate-200 bg-slate-50 px-3 text-sm font-medium text-ink-secondary">
          +91
        </span>
        <input
          type="tel"
          value={mobile}
          maxLength={10}
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
          placeholder="10-digit mobile"
          className="h-[42px] w-full bg-transparent px-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none"
        />
      </div>
      <p className="mt-1 text-xs text-ink-muted">This will be the login ID</p>
      {errors.mobile && <p className="mt-1 text-xs text-danger">{errors.mobile}</p>}
    </div>
  );

  const emailInput = (
    <Input
      label="Email Address"
      required
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      error={errors.email}
      placeholder="owner@example.com"
    />
  );

  const passwordInput = (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[13px] font-medium text-ink-secondary">
          Password <span className="text-danger">*</span>
        </span>
        <button
          type="button"
          onClick={() => setPassword(generatePassword())}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
        >
          <Wand2 className="h-3.5 w-3.5" />
          Generate
        </button>
      </div>
      <Input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
        placeholder="Enter a password"
      />
    </div>
  );

  const shopNameInput = (
    <Input
      label="Shop/Business Name"
      required
      value={shopName}
      onChange={(e) => setShopName(e.target.value)}
      error={errors.shopName}
      placeholder="e.g. Sharma General Store"
    />
  );

  const businessTypeInput = (
    <SearchableSelect
      label="Business Type"
      required
      value={businessType}
      onChange={setBusinessType}
      options={BUSINESS_TYPE_OPTIONS}
      error={errors.businessType}
      placeholder="Select business type..."
    />
  );

  const cityInput = (
    <Input
      label="City"
      required
      value={city}
      onChange={(e) => setCity(e.target.value)}
      error={errors.city}
      placeholder="e.g. Delhi"
    />
  );

  const stateInput = (
    <SearchableSelect
      label="State"
      required
      value={state}
      onChange={setState}
      options={STATE_OPTIONS}
      error={errors.state}
      placeholder="Search states..."
    />
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Retailer" : "Add Retailer"}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            {isEdit ? "Save Changes" : "Add Retailer"}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <section>
          <h4 className="text-sm font-semibold text-ink">Owner Details</h4>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {ownerNameInput}
            {mobileInput}
            {emailInput}
            {!isEdit && passwordInput}
          </div>
        </section>

        <section>
          <h4 className="text-sm font-semibold text-ink">Business Details</h4>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {shopNameInput}
            {businessTypeInput}
            {cityInput}
            {stateInput}
            <div className="sm:col-span-2">
              <Textarea
                label="Full Address"
                rows={2}
                value={fullAddress}
                onChange={(e) => setFullAddress(e.target.value)}
                placeholder="Street address, area, landmark"
              />
            </div>
            <Input
              label="GST Number"
              value={gst}
              onChange={(e) => setGst(e.target.value.toUpperCase())}
              helper="15 characters, optional"
              placeholder="GSTIN"
            />
            {isEdit ? (
              <Input
                label="Device ID"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                helper="Gateway registered device ID"
                placeholder="Device UUID"
              />
            ) : (
              <div className="flex flex-col justify-center rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-ink-secondary">
                <span className="font-semibold text-ink">Device ID</span>
                <span>Auto-registered via Gateway API (<code className="text-primary-600 font-mono text-[11px]">POST /cus/devices/v1</code>) upon creation.</span>
              </div>
            )}
          </div>
        </section>

        <div className="rounded-lg border border-line bg-slate-50 p-4">
          <Toggle
            label="Account Active"
            checked={isActive}
            onChange={setIsActive}
            helper="Inactive retailers cannot log in or create mandates"
          />
        </div>
      </div>
    </Modal>
  );
}